import { createFeatureSelector, createSelector, select } from '@ngrx/store'
import { AppMessages, AppState, ChatOutbox, OutboxEntry, CorpMemberState, FeedDataType, FeedViewType, PostHistoryState, PpasState, SaleType } from '../app.state'
import { isShownInFeed } from '../../utils/sale.util'
import { needsStateCode } from '../../utils/onboarding.util'

export const corpMemberFeatureKey = 'corper'
export const feedFeatureKey = 'feed'
export const messagesFeatureKey = 'messages'
export const postHistoryFeatureKey = 'posted_items'
export const ppasFeatureKey = 'ppas'
export const chatOutboxFeatureKey = 'chat_outbox'

// explanation here https://stackoverflow.com/a/76989238/9259701

export const selectStateCorpMember = (state: AppState) => state.corper;

export const selectFeatureCorpMember =
    createFeatureSelector<CorpMemberState>(corpMemberFeatureKey)

export const selectFeatureFeed =
    createFeatureSelector<FeedDataType>(feedFeatureKey)

export const selectFeaturePostHistory =
    createFeatureSelector<PostHistoryState>(postHistoryFeatureKey)

export const selectFeatureCorpMemberPpaId = createSelector(
    selectFeatureCorpMember,
    (state: CorpMemberState) => state.ppa_id,
)

export const selectFeedView = (view: FeedViewType | null) => createSelector(
    selectFeatureFeed,
    // can make this a switch statement when we expand the view types
    (state: FeedDataType) => view === 'accommodation' ? state.accommodations : feedSales(state.sales),
)

/**
 * Leaves out sales sold more than a day ago: GET /all-items stops sending them, but the store
 * merges fetches, so one it already had would otherwise stay.
 */
function feedSales(sales: Map<number | string, SaleType>): Map<number | string, SaleType> {
    const now = Date.now()
    return new Map([...sales].filter(([, sale]) => isShownInFeed(sale, now)))
}

/** The ids of the sales the logged-in corp member saved; empty until GET /sale/bookmarks answers. */
export const selectSavedSaleIds = createSelector(
    selectFeatureFeed,
    (state: FeedDataType): Set<number> => state.savedSaleIds ?? new Set(),
)

/** The ids of the houses the logged-in corp member saved; empty until GET /accommodation/bookmarks answers. */
export const selectSavedAccommodationIds = createSelector(
    selectFeatureFeed,
    (state: FeedDataType): Set<number> => state.savedAccommodationIds ?? new Set(),
)

export const selectFeatureMessages =
    createFeatureSelector<AppMessages>(messagesFeatureKey)

export const selectChatOutbox =
    createFeatureSelector<ChatOutbox>(chatOutboxFeatureKey)

/**
 * The chats as the sender sees them: each room's saved messages, then the sender's own unsent
 * ones (the chat outbox) in the order they were written.
 */
export const selectMessagesWithOutbox = createSelector(
    selectFeatureMessages,
    selectChatOutbox,
    (messages: AppMessages, outbox: ChatOutbox | undefined): AppMessages => {
        const entries = Object.values(outbox ?? {})
        if (!entries.length) return messages

        const merged = new Map(messages)
        entries
            .sort((a, b) => a.message.created_at.localeCompare(b.message.created_at))
            .forEach((entry) => {
                const room = roomForOutboxEntry(messages, entry)
                const chat = room !== undefined ? merged.get(room) : undefined
                if (!chat) return // its chat isn't loaded (yet) - it's still sent, just not shown
                merged.set(room, { ...chat, texts: [...(chat.texts ?? []), entry.message] })
            })
        return merged
    },
)

/**
 * The room an unsent message shows in: the one it was written in - or, once a new chat's
 * local room has been swapped for the pair's saved one (see updateMessages), that one.
 */
function roomForOutboxEntry(messages: AppMessages, entry: OutboxEntry): string | undefined {
    const room = entry.message.room
    if (messages.has(room)) return room
    const { message_from: me, message_to: them } = entry.outgoing
    if (!them) return undefined
    for (const [key, chat] of messages) {
        const participants = [chat.initiator_id, chat.recipient_id]
        if (typeof key === 'string' && participants.includes(me) && participants.includes(them)) return key
    }
    return undefined
}

export const selectOnboardingChecklistState = createSelector(
    selectFeatureCorpMember,
    (state: CorpMemberState) => ({
        stateCodeDone: !needsStateCode(state),
        wishlistDone: !!state?.onboarding_wishlist_completed_at,
        nameDone: !!state?.first_name?.trim(),
        pushDone: !!state?.onboarding_push_completed_at,
        promptShown: !!state?.onboarding_prompt_shown_at,
        snoozedUntil: state?.onboarding_snoozed_until ?? null,
    }),
)

export const selectFeaturePpas =
    createFeatureSelector<PpasState>(ppasFeatureKey)
